import { UserRole } from '@/types/enums';

/**
 * Permission matrix untuk model role 4-tingkat.
 *
 * Sumber kebenaran tunggal untuk otorisasi sisi klien (route guard, menu,
 * tombol). Selaras dengan matriks pada docs/PLAN.md (FASE 3) dan RLS di
 * supabase/migrations/0003_migrate_roles_and_authorization.sql.
 *
 * Catatan: otorisasi sisi klien hanya untuk UX. Penegakan keamanan sebenarnya
 * tetap berada di RLS Postgres — jangan pernah mengandalkan modul ini sebagai
 * satu-satunya lapisan keamanan.
 */
export type Permission =
  | 'MANAGE_PLATFORM'        // Kelola tenant/bank sampah, monitoring sistem (platform)
  | 'MANAGE_BANK_SAMPAH'     // Kelola pengaturan bank sampah & cabang miliknya
  | 'MANAGE_NASABAH'         // Kelola data & status nasabah
  | 'MANAGE_PENGELOLA'       // Kelola akun pengelola
  | 'MANAGE_PRICING'         // Kelola harga & kategori sampah
  | 'APPROVE_WITHDRAWAL'     // Approve/reject penarikan nasabah
  | 'INPUT_DEPOSIT'          // Input setoran & transaksi
  | 'VIEW_TENANT_REPORTS'    // Lihat laporan operasional bank sampah
  | 'MANAGE_OWN_PROFILE';    // Kelola profil sendiri

const PERMISSIONS_BY_ROLE: Record<UserRole, ReadonlySet<Permission>> = {
  [UserRole.SUPER_ADMIN]: new Set<Permission>([
    'MANAGE_PLATFORM',
    'MANAGE_OWN_PROFILE',
  ]),
  [UserRole.ADMIN_BANK_SAMPAH]: new Set<Permission>([
    'MANAGE_BANK_SAMPAH',
    'MANAGE_NASABAH',
    'MANAGE_PENGELOLA',
    'MANAGE_PRICING',
    'APPROVE_WITHDRAWAL',
    'INPUT_DEPOSIT',
    'VIEW_TENANT_REPORTS',
    'MANAGE_OWN_PROFILE',
  ]),
  [UserRole.PENGELOLA]: new Set<Permission>([
    // Approval withdrawal sementara diberikan ke PENGELOLA sampai ada workflow
    // approval berlapis (lihat catatan FASE 3 di docs/PLAN.md).
    'APPROVE_WITHDRAWAL',
    'INPUT_DEPOSIT',
    // Pengelola cabang mengelola harga & kategori sampah untuk cabangnya
    // (RLS wc_manage sudah mengizinkan berdasarkan managed_branch_ids()).
    'MANAGE_PRICING',
    'VIEW_TENANT_REPORTS',
    'MANAGE_OWN_PROFILE',
  ]),
  [UserRole.NASABAH]: new Set<Permission>([
    'MANAGE_OWN_PROFILE',
  ]),
};

/** Apakah role tertentu memiliki permission yang diminta. */
export function roleCan(role: UserRole | undefined, permission: Permission): boolean {
  if (!role) return false;
  return PERMISSIONS_BY_ROLE[role]?.has(permission) ?? false;
}

/** Apakah role termasuk dalam salah satu role yang diizinkan. */
export function roleIsOneOf(role: UserRole | undefined, allowed: readonly UserRole[]): boolean {
  if (!role) return false;
  return allowed.includes(role);
}

/** Role platform-level (bukan operasional tenant). */
export function isPlatformRole(role: UserRole | undefined): boolean {
  return role === UserRole.SUPER_ADMIN;
}

/** Role yang menjalankan operasional sebuah bank sampah (tenant). */
export function isTenantOperationalRole(role: UserRole | undefined): boolean {
  return role === UserRole.ADMIN_BANK_SAMPAH || role === UserRole.PENGELOLA;
}

/** Label tampilan ramah-pengguna untuk setiap role. */
export const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: 'Super Admin',
  [UserRole.ADMIN_BANK_SAMPAH]: 'Admin Bank Sampah',
  [UserRole.PENGELOLA]: 'Pengelola',
  [UserRole.NASABAH]: 'Nasabah',
};

export function roleLabel(role: UserRole | undefined): string {
  return role ? ROLE_LABELS[role] ?? role : '-';
}
